import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  Chip,
  CircularProgress,
  Alert,
  Drawer,
  TextField,
  MenuItem,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import { Close, Add } from "@mui/icons-material";
import { Edit, Delete } from "@mui/icons-material";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setRisks,
  setTrustWeights,
  setRiskData,
  setLoading as setRiskLoading,
  setError as setRiskError,
} from "../store/slices/riskSlice";
import {
  setApplicationSetupData,
  setLoading as setAppSetupLoading,
} from "../store/slices/applicationSetupSlice";
import { setTrustIndexData } from "../store/slices/trustIndexSlice";
import LoadingOverlay from "../components/LoadingOverlay";
import ProjectRequired from "../components/ProjectRequired";
import { ReactComponent as InputsSVG } from "../asserts/logo/Inputs.svg";
import { ReactComponent as DownloadSVG } from "../asserts/logo/download.svg";

// DOCX export libraries
import { saveAs } from "file-saver";
import {
  Document,
  Packer,
  Paragraph,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  TextRun,
  WidthType,
  AlignmentType,
  BorderStyle,
  HeadingLevel,
  PageBreak,
  VerticalAlign,
  ImageRun,
} from "docx";

// Logo imports for DOCX export
import qualizealLogoPng from "../asserts/logo/QualizealLogo.png";
import validaiteLogoPng from "../asserts/logo/validaite_logo.png";

const baseUrl = process.env.REACT_APP_API_URL || "https://validaite.io/api";
const SIDEBAR_WIDTH = 0;
const RiskIdentification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // const { projectId } = useParams();

  console.log("Inputs from previous stage: ", location.state);

  console.log("Inputs from previous stage: ", location.state);

  // Get data from location.state if coming from navigation
  const {
    applicationName: appNameFromLocation,
    domain: domainFromLocation,
    archetype: archetypeFromLocation,
    riskCode: riskCodeFromLocation,
    riskLevel: riskLevelFromLocation,
    description: descriptionFromLocation,
    modifiers: modifiersFromLocation = [],
    trustWeights: trustWeightsFromLocation,
    useCasesList: useCasesFromLocation = [],
    useCases = [],
    // trustWeights,
  } = location.state || {};

  // Redux state
  const risks = useSelector((state) => state.risk.risks);
  const attributes = useSelector((state) => state.risk.trustWeights);
  const loading = useSelector((state) => state.risk.loading);
  const error = useSelector((state) => state.risk.error);
  const projectId = useSelector((state) => state.project.currentProjectId);

  // Get application setup and trust index data from Redux
  const appSetupBasicInfo = useSelector(
    (state) => state.applicationSetup.basicInfo
  );
  const appSetupClassification = useSelector(
    (state) => state.applicationSetup.classificationResult
  );
  const appSetupUseCases = useSelector(
    (state) => state.applicationSetup.useCasesList
  );
  const trustIndexAttributes = useSelector(
    (state) => state.trustIndex.trustAttributes
  );

  // Use Redux data if available, otherwise use location.state
  const applicationName =
    appNameFromLocation || appSetupBasicInfo.applicationName;
  const domain = domainFromLocation || appSetupBasicInfo.domain;
  const description = descriptionFromLocation || appSetupBasicInfo.description;
  const modifiers =
    modifiersFromLocation.length > 0
      ? modifiersFromLocation
      : appSetupBasicInfo.modifiers;
  const archetype =
    archetypeFromLocation || appSetupClassification?.classification?.archetype;
  const riskCode =
    riskCodeFromLocation || appSetupClassification?.riskProfile?.code;
  const riskLevel =
    riskLevelFromLocation || appSetupClassification?.riskProfile?.level;
  const useCasesList =
    useCasesFromLocation.length > 0 ? useCasesFromLocation : appSetupUseCases;
  const incomingTrustWeights = trustWeightsFromLocation || trustIndexAttributes;

  // Local UI state (not persisted)
  const [identifying, setIdentifying] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const receivedTrustWeights = incomingTrustWeights || {}; // from previous stage
  const [taxonomy, setTaxonomy] = useState({
    categories: [],
    severityLevels: [],
    likelihoodLevels: [],
    owners: [],
    statuses: [],
  });

  const [openDrawer, setOpenDrawer] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [newRisk, setNewRisk] = useState({
    title: "",
    category: "",
    severity: "",
    likelihood: "",
    score: "",
    owner: "",
    status: "",
    description: "",
  });

  // Fetch application setup data if not in Redux
  useEffect(() => {
    const fetchApplicationSetupData = async () => {
      if (!projectId || appSetupBasicInfo.applicationName) return;

      try {
        dispatch(setAppSetupLoading(true));
        const response = await axios.get(
          `${baseUrl}/application-setup?project_id=${projectId}`
        );
        if (response.data.success && response.data.data) {
          dispatch(setApplicationSetupData(response.data.data));
        }
      } catch (err) {
        console.error("Error fetching application setup:", err);
      } finally {
        dispatch(setAppSetupLoading(false));
      }
    };

    fetchApplicationSetupData();
  }, [projectId, appSetupBasicInfo.applicationName, dispatch]);

  // Fetch trust index data if not in Redux
  useEffect(() => {
    const fetchTrustIndexData = async () => {
      if (!projectId || Object.keys(trustIndexAttributes).length > 0) return;

      try {
        const response = await axios.get(
          `${baseUrl}/trust-index-settings?project_id=${projectId}`
        );
        if (response.data.success && response.data.data) {
          dispatch(setTrustIndexData(response.data.data));
        }
      } catch (err) {
        console.error("Error fetching trust index:", err);
      }
    };

    fetchTrustIndexData();
  }, [projectId, trustIndexAttributes, dispatch]);

  // Fetch risk data from database on mount
  useEffect(() => {
    const fetchRiskData = async () => {
      if (!projectId) {
        console.log("No projectId, skipping fetch");
        return;
      }

      try {
        dispatch(setRiskLoading(true));
        const response = await axios.get(
          `${baseUrl}/risk-identification?project_id=${projectId}`
        );

        if (response.data.success && response.data.data) {
          console.log("Fetched risk data:", response.data.data);

          const { attributes, risks, trustWeights } = response.data.data;

          // Map the data properly - use trustWeights if available, otherwise attributes
          const mappedData = {
            risks: risks || [],
            trustWeights: trustWeights || attributes || {},
          };

          dispatch(setRiskData(mappedData));
        }
      } catch (err) {
        console.error("Error fetching risks:", err);
        dispatch(setRiskError("Failed to load data"));
      } finally {
        dispatch(setRiskLoading(false));
      }
    };

    fetchRiskData();
  }, [projectId, dispatch]);

  const handleIdentifyRisks = async () => {
    if (!applicationName || !archetype || !riskLevel || !domain) {
      dispatch(setRiskError("Missing Application Summary data."));
      return;
    }

    if (!projectId) {
      dispatch(
        setRiskError("No project selected. Please select a project first.")
      );
      return;
    }

    setIdentifying(true);
    dispatch(setRiskError(null));
    setSuccessMessage(null);

    try {
      const payload = {
        project_id: projectId,
        applicationSummary: {
          applicationName,
          archetypeCode: archetype,
          riskTier: riskLevel,
          domain,
          description,
        },
        trustWeights: receivedTrustWeights,
        useCasesList: useCases,
        // trustWeights: attributes,
      };

      console.log("📤 Sending payload to backend:", payload);
      console.log("📤 Use cases being sent:", useCases);

      const res = await axios.post(`${baseUrl}/risk-identification`, payload);

      if (res.data.success && res.data.data) {
        dispatch(setRisks(res.data.data.risks || []));
        dispatch(setTrustWeights(res.data.data.attributes || {}));
        setSuccessMessage(`Identified ${res.data.data.risks.length} risks`);
      } else {
        dispatch(setRiskError(res.data.error || "Failed to identify risks."));
      }
    } catch (err) {
      dispatch(setRiskError(err.message));
    } finally {
      setIdentifying(false);
    }
  };

  const handleOpenDrawer = (risk = null, index = null) => {
    if (risk) {
      setNewRisk(risk);
      setEditIndex(index);
    } else {
      setNewRisk({
        title: "",
        category: "",
        severity: "",
        likelihood: "",
        score: "",
        owner: "",
        status: "",
        description: "",
      });
      setEditIndex(null);
    }
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => setOpenDrawer(false);

  const handleSaveRisk = () => {
    let updated;
    if (editIndex !== null) {
      updated = [...risks];
      updated[editIndex] = newRisk;
    } else {
      updated = [...risks, newRisk];
    }
    dispatch(setRisks(updated));
    setOpenDrawer(false);
  };

  const handleDeleteRisk = (index) => {
    const updated = risks.filter((_, i) => i !== index);
    dispatch(setRisks(updated));
  };

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
        }}
      >
        <CircularProgress />
      </Box>
    );
  // === File Download Handler ===
  const handleDownloadFile = async () => {
    try {
      const now = new Date();
      const timestamp = `${String(now.getDate()).padStart(2, "0")}_${String(now.getMonth() + 1).padStart(2, "0")}_${String(now.getFullYear()).slice(-2)}_${String(now.getHours()).padStart(2, "0")}_${String(now.getMinutes()).padStart(2, "0")}_${String(now.getSeconds()).padStart(2, "0")}`;

      // Fetch logo images and convert to ArrayBuffer
      const [qualizealResponse, validaiteResponse] = await Promise.all([
        fetch(qualizealLogoPng),
        fetch(validaiteLogoPng)
      ]);
      const qualizealBuffer = await qualizealResponse.arrayBuffer();
      const validaiteBuffer = await validaiteResponse.arrayBuffer();

      // Create table header row
      const headerRow = new DocxTableRow({
        children: [
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "S.No", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Risk Name", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Category", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Severity", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Likelihood", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          // new DocxTableCell({
          //   children: [new Paragraph({ children: [new TextRun({ text: "Status", bold: true, color: "FFFFFF" })] })],
          //   shading: { fill: "0094FB" },
          // }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Description", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Suggested Mitigation", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
        ],
      });

      // Create table body rows
      const bodyRows = risks.map((r, index) =>
        new DocxTableRow({
          children: [
            new DocxTableCell({ children: [new Paragraph(String(index + 1))] }),
            new DocxTableCell({ children: [new Paragraph(String(r.title || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(r.category || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(r.severity || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(r.likelihood || ""))] }),
            // new DocxTableCell({ children: [new Paragraph(String(r.status || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(r.description || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(r.mitigation || ""))] }),
          ],
        })
      );

      // Create document
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              // Header with Dual Logos - Qualizeal (left) and ValidAIte (right)
              new DocxTable({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.NONE },
                  bottom: { style: BorderStyle.NONE },
                  left: { style: BorderStyle.NONE },
                  right: { style: BorderStyle.NONE },
                  insideHorizontal: { style: BorderStyle.NONE },
                  insideVertical: { style: BorderStyle.NONE },
                },
                rows: [
                  new DocxTableRow({
                    children: [
                      // Left cell - Qualizeal Logo
                      new DocxTableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new ImageRun({
                                data: qualizealBuffer,
                                transformation: {
                                  width: 120,
                                  height: 40,
                                },
                                type: "png",
                              }),
                            ],
                            alignment: AlignmentType.LEFT,
                          }),
                        ],
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        verticalAlign: VerticalAlign.CENTER,
                      }),
                      // Right cell - ValidAIte Logo with tagline
                      new DocxTableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new ImageRun({
                                data: validaiteBuffer,
                                transformation: {
                                  width: 120,
                                  height: 40,
                                },
                                type: "png",
                              }),
                            ],
                            alignment: AlignmentType.RIGHT,
                          }),
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: "Trust First. Risk Aware. Enterprise Ready.",
                                italics: true,
                                size: 16,
                                color: "666666",
                              }),
                            ],
                            alignment: AlignmentType.RIGHT,
                            spacing: { before: 50 },
                          }),
                        ],
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        verticalAlign: VerticalAlign.CENTER,
                      }),
                    ],
                  }),
                ],
              }),
              // Horizontal line
              new Paragraph({
                border: {
                  bottom: { color: "0094FB", space: 1, style: BorderStyle.SINGLE, size: 6 },
                },
                spacing: { after: 400 },
              }),
              // Report Title
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Risk Identification Report",
                    bold: true,
                    size: 36,
                    color: "1A1A1A",
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 300 },
              }),
              // Report Info
              new Paragraph({
                children: [
                  new TextRun({ text: "Application: ", bold: true }),
                  new TextRun({ text: applicationName || "N/A" }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Archetype: ", bold: true }),
                  new TextRun({ text: archetype || "N/A" }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Domain: ", bold: true }),
                  new TextRun({ text: domain || "N/A" }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Risk Tier: ", bold: true }),
                  new TextRun({ text: riskLevel || "N/A" }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Generated: ", bold: true }),
                  new TextRun({ text: now.toLocaleString() }),
                ],
                spacing: { after: 300 },
              }),
              // Risks Section
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Identified Risks (${risks.length})`,
                    bold: true,
                    size: 28,
                    color: "0094FB",
                  }),
                ],
                spacing: { before: 200, after: 200 },
              }),
              // Risks Table
              new DocxTable({
                width: { size: 100, type: WidthType.PERCENTAGE },
                rows: [headerRow, ...bodyRows],
              }),
              // Footer
              new Paragraph({
                children: [
                  new TextRun({
                    text: "\n\n© Qualizeal | ValidAIte - AI Governance & Risk Management Platform",
                    size: 18,
                    color: "888888",
                    italics: true,
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 400 },
              }),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Risk_Identification_Report_${timestamp}.docx`);
    } catch (err) {
      console.error("DOCX export failed:", err);
      alert("Failed to generate report. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: "#f3f2f5ff", // same as TrustIndex page background
        minHeight: "100vh",
        ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* === Sticky Header === */}
      <Box
        sx={{
          position: "sticky",
          top: 50, // stays frozen below top navbar
          zIndex: 100,
          backgroundColor: "#f3f2f5ff",
          py: 2.5,
          px: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600} color="#111827">
          Identify Risks
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Holistic AI Risk & Harm Surface Mapping...
        </Typography>
      </Box>

      {/* === Main Page Content === */}
      <Box sx={{ p: 4, pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {/* === Card 1: Inputs from Previous Stage === */}
        {/* === Card 1: Inputs from Previous Stage === */}
        <Card
          sx={{
            mb: 3,
            p: 3,
            borderRadius: 0,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            mb={2}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <InputsSVG style={{ width: 22, height: 22 }} /> Inputs from Previous
            Stage
          </Typography>

          {/* Application Summary */}
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Application
              </Typography>
              <Typography variant="subtitle2" fontWeight={500}>
                {applicationName || "Not specified"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Archetype Code
              </Typography>
              <Typography variant="subtitle2" fontWeight={500}>
                {archetype || "Not specified"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Risk Tier
              </Typography>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color={
                  riskLevel?.toLowerCase() === "high"
                    ? "#DC2626"
                    : riskLevel?.toLowerCase() === "medium"
                    ? "#F97316"
                    : "#DC2626"
                }
              >
                {riskLevel || "Not specified"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Domain
              </Typography>
              <Typography variant="subtitle2" fontWeight={500}>
                {domain || "Not specified"}
              </Typography>
            </Grid>

            {/* ✅ Modifiers inline like Figma */}
            {/* ✅ Modifiers inline (bold + box style) */}
           {/* ✅ Weights from previous stage */}
<Grid item xs={12}>
  <Typography
    variant="subtitle2"
    fontWeight={700}
    color="#090909"
    sx={{ mb: 1 }}
  >
    Weights:
  </Typography>
 
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: 1.5,
      ml: 0.5,
    }}
  >
    {Object.keys(receivedTrustWeights).length > 0 ? (
      Object.entries(receivedTrustWeights).map(([key, value]) => (
        <Box
          key={key}
          sx={{
            px: 1.5,
            py: 0.4,
            borderRadius: "8px",
            backgroundColor: "#E8F1FF",
            color: "#0094FB",
            fontWeight: 600,
            fontSize: "0.85rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          {key}: {value}
        </Box>
      ))
    ) : (
      <Typography>No weights provided</Typography>
    )}
  </Box>
</Grid>
          </Grid>
        </Card>
        {/* === Identify Risks Button (Between Cards) === */}
        <Box display="flex" justifyContent="flex-start" mb={3} ml={0.5}>
<Button
  variant="contained"
  color="primary"
  onClick={handleIdentifyRisks}
  disabled={identifying}
  startIcon={identifying ? <CircularProgress size={16} /> : null}
  sx={{
    borderRadius: 2,
    textTransform: "none",
    px: 3,
    fontWeight: 700,
    bgcolor: "#3B82F6",
    "&:hover": { bgcolor: "#2563EB" },
  }}
>
  {identifying ? "Identifying..." : "Generate Risk Register"}
</Button>

        </Box>

        {/* === Card 2: Attributes === */}
        {/* === Card 2: Attributes === */}
        {/* === Card 2: Attributes === */}
        <Card
          sx={{
            mb: 3,
            p: 3,
            borderRadius: 0,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography fontWeight={700}>Attributes/Risks</Typography>

            <Button
              variant="text"
              size="small"
              sx={{
                color: "#3B82F6",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.85rem",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": {
                  textDecoration: "underline",
                  backgroundColor: "transparent",
                },
              }}
              onClick={() => console.log("View Taxonomy Details clicked")}
            >
              {/* <i
                className="fas fa-info-circle"
                style={{ fontSize: "14px", color: "#3B82F6" }}
              ></i>
              View Taxonomy Details */}
            </Button>
          </Box>

          {/* Attributes Grid */}
          <Grid
            container
            spacing={5} // ⬅️ increased horizontal space between attributes
            sx={{
              justifyContent: "flex-start", // start from left
              textAlign: "left",
            }}
          >
            {Object.keys(attributes).map((cat, i) => (
              <Grid
                item
                xs={6}
                sm={4}
                md={2.8} // ⬅️ controls how many items per row (3–4 max)
                key={i}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="#6b7280"
                    sx={{
                      mb: 0.5,
                      fontWeight: 500,
                    }}
                  >
                    {cat}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color="#000"
                    sx={{
                      fontSize: "1rem",
                      lineHeight: 1.3,
                    }}
                  >
                    {attributes[cat]}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* === Card 3: Risk Register === */}
        {/* === Card 3: Risk Register === */}
        <Card
          sx={{
            p: 3,
            borderRadius: 0,
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography fontWeight="bold">
              Risk Register ({risks.length} Risks)
            </Typography>

            <Box display="flex" alignItems="center" gap={2}>
              {/* 🔍 Search box */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 0.5,
                  bgcolor: "#F9FAFB",
                  width: 250,
                }}
              >
                <i
                  className="fas fa-search"
                  style={{
                    color: "#6B7280",
                    fontSize: "0.9rem",
                    marginRight: "8px",
                  }}
                />
                <TextField
                  placeholder="Search"
                  variant="standard"
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: "0.9rem", color: "#374151" },
                  }}
                />
              </Box>

              <Button
                startIcon={<Add />}
                sx={{
                  textTransform: "none",
                  color: "#0094FB",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
                onClick={() => handleOpenDrawer()}
              >
                Add more risks
              </Button>
            </Box>
          </Box>

          {/* ✅ Scrollable Table */}
          {/* Filter risks based on search query */}
          {(() => {
            const filteredRisks = risks.filter(
              (risk) =>
                risk.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                risk.category
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                risk.severity
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                risk.likelihood
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                risk.owner?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                risk.status
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                risk.description
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase())
            );

            return filteredRisks.length === 0 ? (
              <Box
                textAlign="center"
                py={5}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    backgroundColor: "#EFF6FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="h4" color="#3B82F6">
                    ⓘ
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight={600} color="#111827">
                  {risks.length === 0
                    ? "No risks identified yet"
                    : "No matching risks found"}
                </Typography>
                <Typography variant="body2" color="#6B7280">
                  {risks.length === 0
                    ? 'Click "Identify Risks" to generate via LLM or add custom risks.'
                    : "Try adjusting your search query."}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  maxHeight: 250, // 👈 Controls visible area height (scroll if more)
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    textAlign: "left",
                    fontSize: "0.9rem",
                  }}
                >
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "#fff",
                      zIndex: 1,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    }}
                  >
                    <tr>
                      {[
                        "Risk Name",
                        "Category",
                        "Severity",
                        "Likelihood",
                        "Owner",
                        "Status",
                        "Actions",
                      ].map((header, i) => (
                        <th
                          key={i}
                          style={{
                            padding: "12px 10px",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            color: "#374151",
                            whiteSpace: "nowrap",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          {header} <span style={{ color: "#9CA3AF" }}></span>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRisks.map((risk, index) => (
                      <tr
                        key={index}
                        style={{
                          borderBottom: "1px solid #E5E7EB",
                          backgroundColor: index % 2 === 0 ? "#fff" : "#F9FAFB",
                        }}
                      >
                        <td
                          style={{
                            padding: "12px 10px",
                            color: "#0094FB",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          <Tooltip
                            title={risk.description || "No description available"}
                            arrow
                            placement="top"
                          >
                            <span>{risk.title}</span>
                          </Tooltip>
                        </td>
                        <td style={{ padding: "12px 10px" }}>
                          {risk.category}
                        </td>

                        {/* Severity Chip */}
                        <td style={{ padding: "12px 10px" }}>
                          <Chip
                            label={risk.severity}
                            sx={{
                              bgcolor:
                                risk.severity === "High"
                                  ? "#FEE2E2"
                                  : risk.severity === "Medium"
                                  ? "#FEF3C7"
                                  : "#DCFCE7",
                              color:
                                risk.severity === "High"
                                  ? "#B91C1C"
                                  :risk.severity === "Low"
                                  ? "#166534"
                                  :risk.severity === "Critical"
                                  ? "#B91C1C"
                                  : risk.severity === "Medium"
                                  ? "#92400E"
                                  : "#B91C1C",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              px: 0.5,
                            }}
                          />
                        </td>

                        {/* Likelihood Chip */}
                        <td style={{ padding: "12px 10px" }}>
                          <Chip
                            label={risk.likelihood}
                            sx={{
                              bgcolor:
                                risk.likelihood === "High"
                                  ? "#FEE2E2"
                                  : risk.likelihood === "Medium"
                                  ? "#FEF3C7"
                                  : "#DBEAFE",
                              color:
                                risk.likelihood === "High"
                                  ? "#B91C1C"
                                  : risk.likelihood === "Medium"
                                  ? "#92400E"
                                  : "#1E3A8A",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              px: 0.5,
                            }}
                          />
                        </td>

                        <td style={{ padding: "12px 10px" }}>{risk.owner}</td>
                        <td style={{ padding: "12px 10px" }}>{risk.status}</td>

                        {/* ✏️ and 🗑️ Actions */}
                        <td style={{ padding: "12px 10px" }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <IconButton
                              size="small"
                              sx={{ color: "#2563EB" }}
                              onClick={() => handleOpenDrawer(risk, index)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: "#DC2626" }}
                              onClick={() => handleDeleteRisk(index)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            );
          })()}
        </Card>
      </Box>
      {/* === Drawer for Add/Edit Risk === */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: { width: 400, p: 3, borderRadius: "12px 0 0 12px" },
        }}
      >
        <Typography variant="h6" fontWeight={600} mb={2}>
          {editIndex !== null ? "Edit Risk" : "Add New Risk"}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Title"
              fullWidth
              value={newRisk.title}
              onChange={(e) =>
                setNewRisk({ ...newRisk, title: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Category"
              fullWidth
              value={newRisk.category}
              onChange={(e) =>
                setNewRisk({ ...newRisk, category: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Severity"
              select
              fullWidth
              value={newRisk.severity}
              onChange={(e) =>
                setNewRisk({ ...newRisk, severity: e.target.value })
              }
            >
              {["Low", "Medium", "High"].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Likelihood"
              select
              fullWidth
              value={newRisk.likelihood}
              onChange={(e) =>
                setNewRisk({ ...newRisk, likelihood: e.target.value })
              }
            >
              {["Low", "Medium", "High"].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Owner"
              fullWidth
              value={newRisk.owner}
              onChange={(e) =>
                setNewRisk({ ...newRisk, owner: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Status"
              select
              fullWidth
              value={newRisk.status}
              onChange={(e) =>
                setNewRisk({ ...newRisk, status: e.target.value })
              }
            >
              {["Draft", "In Progress", "Resolved"].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              multiline
              rows={3}
              fullWidth
              value={newRisk.description}
              onChange={(e) =>
                setNewRisk({ ...newRisk, description: e.target.value })
              }
            />
          </Grid>
        </Grid>

        {/* Drawer Footer Buttons */}
        <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
          <Button variant="outlined" onClick={handleCloseDrawer}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRisk}
            sx={{
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1E40AF" },
            }}
          >
            {editIndex !== null ? "Save Changes" : "Add Risk"}
          </Button>
        </Box>
      </Drawer>

      {/* === Bottom Action Buttons (Dynamic Position) === */}
      {/* === Bottom Buttons Aligned with Table === */}
      {/* === Bottom Buttons (slightly left-aligned under table) === */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end", // still right-aligned within table width
          gap: 2,
          mt: 3, // ⬆️ adds space ABOVE the buttons
          mb: 5, // ⬇️ adds space BELOW the buttons (away from page bottom)
          pr: 6, // slight indentation from right edge
        }}
      >
        <Button
          variant="outlined"
          startIcon={<DownloadSVG style={{ width: 20, height: 20 }} />}
          onClick={handleDownloadFile}
          sx={{
            borderColor: "#3B82F6",
            color: "#3B82F6",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: 2,
            px: 3,
            py: 1,
            "&:hover": { bgcolor: "#EBF5FF", borderColor: "#2563EB" },
          }}
        >
          Download File
        </Button>

        <Button
          variant="contained"
          onClick={() =>
            navigate(`/project/${projectId}/metrics-definition`, {
              state: {
                applicationSummary: {
                  applicationName,
                  domain,
                  archetypeCode: archetype,
                  riskTier: riskLevel,
                  description,
                  modifiers,
                },
                trustWeights: attributes,
                risks: risks,
                useCases: useCases,
              },
            })
          }
          disabled={risks.length === 0}
          sx={{
            bgcolor: "#3B82F6",
            textTransform: "none",
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            "&:hover": { bgcolor: "#2563EB" },
            "&.Mui-disabled": {
              bgcolor: "#D1D5DB",
              color: "#9CA3AF",
            },
          }}
        >
          Go to Define Metrics for Risks →
        </Button>
      </Box>

      {/* Loading Overlay */}
      <LoadingOverlay open={identifying} message="Identifying Risks..." />
    </Box>
  );
};

export default RiskIdentification;

// import React, { useState, useEffect } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Paper,
//   Button,
//   Card,
//   CircularProgress,
//   Alert,
//   Drawer,
//   TextField,
//   MenuItem,
//   IconButton,
// } from "@mui/material";
// import { Close, Add } from "@mui/icons-material";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";

// const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// const SIDEBAR_WIDTH = 0;

// const RiskIdentification = () => {
//   const navigate = useNavigate();
//   const location = useLocation();

//   const {
//     applicationName,
//     domain,
//     archetype,
//     riskCode,
//     riskLevel,
//     description,
//     trustWeights,
//   } = location.state || {};

//   const [loading, setLoading] = useState(false);
//   const [identifying, setIdentifying] = useState(false);
//   const [error, setError] = useState(null);
//   const [successMessage, setSuccessMessage] = useState(null);
//   const [risks, setRisks] = useState([]);
//   const [attributes, setAttributes] = useState(
//     trustWeights || {
//       Accuracy: 0,
//       Fairness: 0,
//       Privacy: 0,
//       Ethics: 0,
//       Robustness: 0,
//       Security: 0,
//       Reliability: 0,
//     }
//   );
//   const [taxonomy, setTaxonomy] = useState({
//     categories: [],
//     severityLevels: [],
//     likelihoodLevels: [],
//     owners: [],
//     statuses: [],
//   });

//   const [openDrawer, setOpenDrawer] = useState(false);
//   const [editIndex, setEditIndex] = useState(null);
//   const [newRisk, setNewRisk] = useState({
//     title: "",
//     category: "",
//     severity: "",
//     likelihood: "",
//     score: "",
//     owner: "",
//     status: "",
//     description: "",
//   });

//   useEffect(() => {
//     const fetchTaxonomyAndData = async () => {
//       setLoading(true);
//       try {
//         const res = await axios.get(`${baseUrl}/risk-identification`);
//         if (res.data.success) {
//           setRisks(res.data.data?.risks || []);
//           setTaxonomy(res.data.taxonomy || taxonomy);
//         }
//       } catch (err) {
//         setError("Failed to fetch risks or taxonomy.");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchTaxonomyAndData();
//   }, []);

//   const handleIdentifyRisks = async () => {
//     if (!applicationName || !archetype || !riskLevel || !domain) {
//       setError("Missing Application Summary data.");
//       return;
//     }
//     setIdentifying(true);
//     setError(null);
//     setSuccessMessage(null);

//     try {
//       const payload = {
//         applicationSummary: {
//           applicationName,
//           archetypeCode: archetype,
//           riskTier: riskLevel,
//           domain,
//           description,
//         },
//         trustWeights: attributes,
//       };

//       const res = await axios.post(`${baseUrl}/risk-identification`, payload);

//       if (res.data.success && res.data.data) {
//         setRisks(res.data.data.risks || []);
//         setAttributes(res.data.data.attributes || attributes);
//         setSuccessMessage(`✅ Identified ${res.data.data.risks.length} risks`);
//       } else {
//         setError(res.data.error || "Failed to identify risks.");
//       }
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setIdentifying(false);
//     }
//   };

//   const handleOpenDrawer = (risk = null, index = null) => {
//     if (risk) {
//       setNewRisk(risk);
//       setEditIndex(index);
//     } else {
//       setNewRisk({
//         title: "",
//         category: "",
//         severity: "",
//         likelihood: "",
//         score: "",
//         owner: "",
//         status: "",
//         description: "",
//       });
//       setEditIndex(null);
//     }
//     setOpenDrawer(true);
//   };

//   const handleCloseDrawer = () => setOpenDrawer(false);

//   const handleSaveRisk = () => {
//     if (editIndex !== null) {
//       const updated = [...risks];
//       updated[editIndex] = newRisk;
//       setRisks(updated);
//     } else {
//       setRisks([...risks, newRisk]);
//     }
//     setOpenDrawer(false);
//   };

//   const handleDeleteRisk = (index) => {
//     const updated = risks.filter((_, i) => i !== index);
//     setRisks(updated);
//   };

//   if (loading)
//     return (
//       <Box
//         sx={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           height: "100vh",
//           ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//         }}
//       >
//         <CircularProgress />
//       </Box>
//     );

//   return (
//     <Box
//       sx={{
//         flex: 1,
//         bgcolor: "#fafafa",
//         minHeight: "100vh",
//         ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//         fontFamily: "'Inter', sans-serif",
//       }}
//     >
//       {/* Sticky Header */}
//       <Box
//         sx={{
//           position: "sticky",
//           top: 60, // adjust if you have an AppHeader height
//           zIndex: 100,
//           backgroundColor: "#fafafa",
//           py: 2,
//           px: 4,
//           borderBottom: "1px solid #E5E7EB",
//         }}
//       >
//         <Typography variant="h5" fontWeight={600} color="#111827">
//           Risk Identification & Classification
//         </Typography>
//         <Typography variant="body2" color="text.secondary">
//           Identify and classify potential risks to establish your risk register
//         </Typography>
//         <Box sx={{ mt: 1, borderBottom: "1px solid #E5E7EB" }}></Box>
//       </Box>

//       {/* Scrollable Content */}
//       <Box
//         sx={{
//           p: 4,
//           pt: 5,
//           overflowY: "auto",
//           minHeight: "calc(100vh - 60px)",
//         }}
//       >
//         {error && (
//           <Alert severity="error" sx={{ mb: 3 }}>
//             {error}
//           </Alert>
//         )}
//         {successMessage && (
//           <Alert severity="success" sx={{ mb: 3 }}>
//             {successMessage}
//           </Alert>
//         )}

//         {/* Application Summary */}
//         <Card
//           sx={{
//             mb: 3,
//             p: 2,
//             borderRadius: 3,
//             boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
//             bgcolor: "#fff",
//           }}
//         >
//           <Typography variant="h6" gutterBottom>
//             Application Summary
//           </Typography>
//           <Grid container spacing={2}>
//             <Grid item xs={6} md={3}>
//               <Typography variant="body2" color="textSecondary">
//                 Application
//               </Typography>
//               <Typography variant="subtitle2">
//                 {applicationName || "Not specified"}
//               </Typography>
//             </Grid>
//             <Grid item xs={6} md={3}>
//               <Typography variant="body2" color="textSecondary">
//                 Archetype Code
//               </Typography>
//               <Typography variant="subtitle2">
//                 {archetype || "Not specified"}
//               </Typography>
//             </Grid>
//             <Grid item xs={6} md={3}>
//               <Typography variant="body2" color="textSecondary">
//                 Risk Tier
//               </Typography>
//               <Typography variant="subtitle2" color="error">
//                 {riskLevel || "Not specified"}
//               </Typography>
//             </Grid>
//             <Grid item xs={6} md={3}>
//               <Typography variant="body2" color="textSecondary">
//                 Domain
//               </Typography>
//               <Typography variant="subtitle2">{domain}</Typography>
//             </Grid>
//           </Grid>
//         </Card>

//         {/* Identify Button */}
//         <Box display="flex" justifyContent="flex-end" mb={2}>
//           <Button
//             variant="contained"
//             color="primary"
//             onClick={handleIdentifyRisks}
//             disabled={identifying}
//             startIcon={identifying ? <CircularProgress size={16} /> : null}
//           >
//             {identifying ? "Identifying..." : "Identify Risks"}
//           </Button>
//         </Box>

//         {/* Attributes */}
//         <Paper
//           sx={{
//             p: 3,
//             mb: 3,
//             borderRadius: 2,
//             boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
//           }}
//         >
//           <Typography fontWeight="bold" mb={2}>
//             Attributes
//           </Typography>
//           <Grid container spacing={2}>
//             {Object.keys(attributes).map((cat, i) => (
//               <Grid item xs={6} sm={3} md={1.7} key={i}>
//                 <Box textAlign="center">
//                   <Typography variant="body2" fontWeight="bold">
//                     {cat}
//                   </Typography>
//                   <Typography variant="h6" color="text.secondary">
//                     {attributes[cat]}
//                   </Typography>
//                 </Box>
//               </Grid>
//             ))}
//           </Grid>
//         </Paper>

//         {/* Risk Register Table */}
//         <Card sx={{ p: 3, borderRadius: 2, backgroundColor: "#fff" }}>
//           <Box display="flex" justifyContent="space-between" mb={2}>
//             <Typography fontWeight="bold">
//               Risk Register ({risks.length} Risks)
//             </Typography>
//             <Button
//               startIcon={<Add />}
//               sx={{ textTransform: "none" }}
//               onClick={() => handleOpenDrawer()}
//             >
//               + Add more risks
//             </Button>
//           </Box>

//           <Box sx={{ overflowX: "auto" }}>
//             <table style={{ width: "100%", borderCollapse: "collapse" }}>
//               <thead>
//                 <tr
//                   style={{
//                     borderBottom: "1px solid #e0e0e0",
//                     background: "#fafafa",
//                   }}
//                 >
//                   {[
//                     "Risk Name",
//                     "Category",
//                     "Severity",
//                     "Likelihood",
//                     "Score",
//                     "Owner",
//                     "Status",
//                     "Actions",
//                   ].map((header, i) => (
//                     <th
//                       key={i}
//                       style={{
//                         padding: "10px 8px",
//                         fontWeight: 600,
//                         textAlign: "left",
//                       }}
//                     >
//                       {header}
//                     </th>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody>
//                 {risks.length > 0 ? (
//                   risks.map((risk, index) => (
//                     <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
//                       <td style={{ padding: "10px 8px", color: "#1976d2" }}>
//                         {risk.title}
//                       </td>
//                       <td style={{ padding: "10px 8px" }}>{risk.category}</td>
//                       <td style={{ padding: "10px 8px" }}>{risk.severity}</td>
//                       <td style={{ padding: "10px 8px" }}>{risk.likelihood}</td>
//                       <td style={{ padding: "10px 8px" }}>{risk.score}</td>
//                       <td style={{ padding: "10px 8px" }}>{risk.owner}</td>
//                       <td style={{ padding: "10px 8px" }}>{risk.status}</td>
//                       <td
//                         style={{
//                           display: "flex",
//                           gap: "6px",
//                           padding: "10px 8px",
//                         }}
//                       >
//                         <Button
//                           size="small"
//                           sx={{ color: "#1976d2" }}
//                           onClick={() => handleOpenDrawer(risk, index)}
//                         >
//                           ✏️
//                         </Button>
//                         <Button
//                           size="small"
//                           sx={{ color: "#d32f2f" }}
//                           onClick={() => handleDeleteRisk(index)}
//                         >
//                           🗑️
//                         </Button>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td
//                       colSpan={8}
//                       style={{ textAlign: "center", padding: "20px" }}
//                     >
//                       No risks identified yet.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </Box>
//         </Card>

//         {/* Drawer for Add/Edit */}
//         <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
//           <Box sx={{ width: 400, p: 3 }}>
//             <Box display="flex" justifyContent="space-between" mb={2}>
//               <Typography variant="h6">
//                 {editIndex !== null ? "Edit Risk" : "Add Risk"}
//               </Typography>
//               <IconButton onClick={handleCloseDrawer}>
//                 <Close />
//               </IconButton>
//             </Box>

//             <TextField
//               label="Risk Name"
//               fullWidth
//               value={newRisk.title}
//               onChange={(e) =>
//                 setNewRisk({ ...newRisk, title: e.target.value })
//               }
//               sx={{ mb: 2 }}
//             />

//             <TextField
//               select
//               fullWidth
//               label="Category"
//               value={newRisk.category}
//               onChange={(e) =>
//                 setNewRisk({ ...newRisk, category: e.target.value })
//               }
//               sx={{ mb: 2 }}
//             >
//               {taxonomy.categories.map((c) => (
//                 <MenuItem key={c} value={c}>
//                   {c}
//                 </MenuItem>
//               ))}
//             </TextField>

//             <TextField
//               select
//               fullWidth
//               label="Severity"
//               value={newRisk.severity}
//               onChange={(e) =>
//                 setNewRisk({ ...newRisk, severity: e.target.value })
//               }
//               sx={{ mb: 2 }}
//             >
//               {taxonomy.severityLevels.map((s) => (
//                 <MenuItem key={s} value={s}>
//                   {s}
//                 </MenuItem>
//               ))}
//             </TextField>

//             <TextField
//               select
//               fullWidth
//               label="Likelihood"
//               value={newRisk.likelihood}
//               onChange={(e) =>
//                 setNewRisk({ ...newRisk, likelihood: e.target.value })
//               }
//               sx={{ mb: 2 }}
//             >
//               {taxonomy.likelihoodLevels.map((l) => (
//                 <MenuItem key={l} value={l}>
//                   {l}
//                 </MenuItem>
//               ))}
//             </TextField>

//             <TextField
//               label="Score"
//               fullWidth
//               value={newRisk.score}
//               onChange={(e) =>
//                 setNewRisk({ ...newRisk, score: e.target.value })
//               }
//               sx={{ mb: 2 }}
//             />

//             <TextField
//               select
//               fullWidth
//               label="Owner"
//               value={newRisk.owner}
//               onChange={(e) =>
//                 setNewRisk({ ...newRisk, owner: e.target.value })
//               }
//               sx={{ mb: 2 }}
//             >
//               {taxonomy.owners.map((o) => (
//                 <MenuItem key={o} value={o}>
//                   {o}
//                 </MenuItem>
//               ))}
//             </TextField>

//             <TextField
//               select
//               fullWidth
//               label="Status"
//               value={newRisk.status}
//               onChange={(e) =>
//                 setNewRisk({ ...newRisk, status: e.target.value })
//               }
//               sx={{ mb: 2 }}
//             >
//               {taxonomy.statuses.map((s) => (
//                 <MenuItem key={s} value={s}>
//                   {s}
//                 </MenuItem>
//               ))}
//             </TextField>

//             <TextField
//               label="Description"
//               fullWidth
//               multiline
//               minRows={3}
//               value={newRisk.description}
//               onChange={(e) =>
//                 setNewRisk({ ...newRisk, description: e.target.value })
//               }
//               sx={{ mb: 3 }}
//             />

//             <Button
//               variant="contained"
//               color="primary"
//               fullWidth
//               onClick={handleSaveRisk}
//             >
//               {editIndex !== null ? "Update" : "Add"} Risk
//             </Button>
//           </Box>
//         </Drawer>

//         {/* Navigation Buttons */}
//         <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
//           <Button variant="outlined">Save Draft</Button>
//           <Button
//             variant="contained"
//             color="primary"
//             onClick={() =>
//               navigate("/metrics-definition", {
//                 state: {
//                   applicationSummary: {
//                     applicationName,
//                     archetypeCode: archetype,
//                     riskTier: riskLevel,
//                     domain,
//                     description,
//                   },
//                   trustWeights: attributes,
//                   risks,
//                 },
//               })
//             }
//           >
//             Metrics Definition →
//           </Button>
//         </Box>
//       </Box>
//     </Box>
//   );
// };

// export default RiskIdentification;

// import React, { useState, useEffect } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Paper,
//   Button,
//   Chip,
//   Divider,
//   Stack,
//   Card,
//   CardContent,
//   CircularProgress,
//   Alert,
//   Drawer,
//   TextField,
//   MenuItem,
//   IconButton,
// } from "@mui/material";
// import { Close, Add } from "@mui/icons-material";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";

// const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// const SIDEBAR_WIDTH = 0;

// const RiskIdentification = () => {
//   const navigate = useNavigate();
//   const location = useLocation();

//   const {
//     applicationName,
//     domain,
//     archetype,
//     riskCode,
//     riskLevel,
//     description,
//     trustWeights,
//   } = location.state || {};

//   const [loading, setLoading] = useState(false);
//   const [identifying, setIdentifying] = useState(false);
//   const [error, setError] = useState(null);
//   const [successMessage, setSuccessMessage] = useState(null);
//   const [risks, setRisks] = useState([]);
//   const [attributes, setAttributes] = useState(
//     trustWeights || {
//       Accuracy: 0,
//       Fairness: 0,
//       Privacy: 0,
//       Ethics: 0,
//       Robustness: 0,
//       Security: 0,
//       Reliability: 0,
//     }
//   );
//   const [severityCounts, setSeverityCounts] = useState({});
//   const [taxonomy, setTaxonomy] = useState({
//     categories: [],
//     severityLevels: [],
//     likelihoodLevels: [],
//     owners: [],
//     statuses: [],
//   });

//   // Drawer state
//   const [openDrawer, setOpenDrawer] = useState(false);
//   const [editIndex, setEditIndex] = useState(null);
//   const [newRisk, setNewRisk] = useState({
//     title: "",
//     category: "",
//     severity: "",
//     likelihood: "",
//     score: "",
//     owner: "",
//     status: "",
//     description: "",
//   });

//   useEffect(() => {
//     const fetchTaxonomyAndData = async () => {
//       setLoading(true);
//       try {
//         const res = await axios.get(`${baseUrl}/risk-identification`);
//         if (res.data.success) {
//           setRisks(res.data.data?.risks || []);
//           setTaxonomy(res.data.taxonomy || taxonomy);
//         }
//       } catch (err) {
//         setError("Failed to fetch risks or taxonomy.");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchTaxonomyAndData();
//   }, []);

//   // Update counts for Score Distribution
//   useEffect(() => {
//     const severities = {};
//     taxonomy.severityLevels.forEach((s) => (severities[s] = 0));
//     risks.forEach((r) => {
//       if (r.severity) severities[r.severity] = (severities[r.severity] || 0) + 1;
//     });
//     setSeverityCounts(severities);
//   }, [risks, taxonomy]);

//   // Identify Risks
//   const handleIdentifyRisks = async () => {
//     if (!applicationName || !archetype || !riskLevel || !domain) {
//       setError("Missing Application Summary data.");
//       return;
//     }
//     setIdentifying(true);
//     try {
//       const payload = {
//         applicationSummary: {
//           applicationName,
//           archetypeCode: archetype,
//           riskTier: riskLevel,
//           domain,
//           description,
//         },
//         trustWeights: attributes,
//       };
//       const res = await axios.post(`${baseUrl}/risk-identification`, payload);
//       if (res.data.success && res.data.data) {
//         setRisks(res.data.data.risks || []);
//         setAttributes(res.data.data.attributes || attributes);
//         setSuccessMessage(`✅ Identified ${res.data.data.risks.length} risks`);
//       } else setError(res.data.error || "Failed to identify risks.");
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setIdentifying(false);
//     }
//   };

//   // Drawer logic
//   const handleOpenDrawer = (risk = null, index = null) => {
//     if (risk) {
//       setNewRisk(risk);
//       setEditIndex(index);
//     } else {
//       setNewRisk({
//         title: "",
//         category: "",
//         severity: "",
//         likelihood: "",
//         score: "",
//         owner: "",
//         status: "",
//         description: "",
//       });
//       setEditIndex(null);
//     }
//     setOpenDrawer(true);
//   };

//   const handleCloseDrawer = () => setOpenDrawer(false);

//   const handleSaveRisk = () => {
//     if (editIndex !== null) {
//       const updatedRisks = [...risks];
//       updatedRisks[editIndex] = newRisk;
//       setRisks(updatedRisks);
//     } else {
//       setRisks([...risks, newRisk]);
//     }
//     setOpenDrawer(false);
//   };

//   const handleDeleteRisk = (index) => {
//     const updated = risks.filter((_, i) => i !== index);
//     setRisks(updated);
//   };

//   if (loading)
//     return (
//       <Box
//         sx={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           height: "100vh",
//           ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//         }}
//       >
//         <CircularProgress />
//       </Box>
//     );

//   return (
//     <Box
//       sx={{
//         flex: 1,
//         p: 4,
//         overflow: "auto",
//         bgcolor: "#fafafa",
//         minHeight: "100vh",
//         ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//       }}
//     >
//       <Typography variant="h5" fontWeight="bold" mb={1}>
//         Risk Identification & Classification
//       </Typography>
//       <Typography variant="body2" color="text.secondary" mb={3}>
//         Identify and classify potential risks to establish your risk register
//       </Typography>

//       {error && (
//         <Alert severity="error" sx={{ mb: 3 }}>
//           {error}
//         </Alert>
//       )}
//       {successMessage && (
//         <Alert severity="success" sx={{ mb: 3 }}>
//           {successMessage}
//         </Alert>
//       )}

//       {/* Application Summary */}
//       <Card sx={{ mb: 3, p: 2 }}>
//         <Typography variant="h6" gutterBottom>
//           Application Summary
//         </Typography>
//         <Grid container spacing={2}>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">
//               Application
//             </Typography>
//             <Typography variant="subtitle2">
//               {applicationName || "Not specified"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">
//               Archetype Code
//             </Typography>
//             <Typography variant="subtitle2">
//               {archetype || "Not specified"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">
//               Risk Tier
//             </Typography>
//             <Typography variant="subtitle2" color="error">
//               {riskLevel || "Not specified"}
//             </Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">
//               Domain
//             </Typography>
//             <Typography variant="subtitle2">{domain}</Typography>
//           </Grid>
//         </Grid>
//       </Card>

//       {/* Identify Button */}
//       <Box display="flex" justifyContent="flex-end" mb={2}>
//         <Button
//           variant="contained"
//           color="primary"
//           onClick={handleIdentifyRisks}
//           disabled={identifying}
//           startIcon={identifying ? <CircularProgress size={16} /> : null}
//         >
//           {identifying ? "Identifying..." : "Identify Risks"}
//         </Button>
//       </Box>

//       {/* Attributes */}
//       <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
//         <Typography fontWeight="bold" mb={2}>
//           Attributes
//         </Typography>
//         <Grid container spacing={2}>
//           {Object.keys(attributes).map((cat, i) => (
//             <Grid item xs={6} sm={3} md={1.7} key={i}>
//               <Box textAlign="center">
//                 <Typography variant="body2" fontWeight="bold">
//                   {cat}
//                 </Typography>
//                 <Typography variant="h6" color="text.secondary">
//                   {attributes[cat]}
//                 </Typography>
//               </Box>
//             </Grid>
//           ))}
//         </Grid>
//       </Paper>

//       {/* Risk Register Table */}
//       <Card sx={{ p: 3, borderRadius: 2, backgroundColor: "#fff" }}>
//         <Box display="flex" justifyContent="space-between" mb={2}>
//           <Typography fontWeight="bold">
//             Risk Register ({risks.length} Risks)
//           </Typography>
//           <Button
//             startIcon={<Add />}
//             sx={{ textTransform: "none" }}
//             onClick={() => handleOpenDrawer()}
//           >
//             + Add more risks
//           </Button>
//         </Box>

//         <Box sx={{ overflowX: "auto" }}>
//           <table style={{ width: "100%", borderCollapse: "collapse" }}>
//             <thead>
//               <tr
//                 style={{
//                   borderBottom: "1px solid #e0e0e0",
//                   background: "#fafafa",
//                 }}
//               >
//                 {[
//                   "Risk Name",
//                   "Category",
//                   "Severity",
//                   "Likelihood",
//                   "Score",
//                   "Owner",
//                   "Status",
//                   "Actions",
//                 ].map((header, i) => (
//                   <th
//                     key={i}
//                     style={{
//                       padding: "10px 8px",
//                       fontWeight: 600,
//                       textAlign: "left",
//                     }}
//                   >
//                     {header}
//                   </th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody>
//               {risks.length > 0 ? (
//                 risks.map((risk, index) => (
//                   <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
//                     <td style={{ padding: "10px 8px", color: "#1976d2" }}>
//                       {risk.title}
//                     </td>
//                     <td style={{ padding: "10px 8px" }}>{risk.category}</td>
//                     <td style={{ padding: "10px 8px" }}>{risk.severity}</td>
//                     <td style={{ padding: "10px 8px" }}>{risk.likelihood}</td>
//                     <td style={{ padding: "10px 8px" }}>{risk.score}</td>
//                     <td style={{ padding: "10px 8px" }}>{risk.owner}</td>
//                     <td style={{ padding: "10px 8px" }}>{risk.status}</td>
//                     <td
//                       style={{
//                         display: "flex",
//                         gap: "6px",
//                         padding: "10px 8px",
//                       }}
//                     >
//                       <Button
//                         size="small"
//                         sx={{ color: "#1976d2" }}
//                         onClick={() => handleOpenDrawer(risk, index)}
//                       >
//                         ✏️
//                       </Button>
//                       <Button
//                         size="small"
//                         sx={{ color: "#d32f2f" }}
//                         onClick={() => handleDeleteRisk(index)}
//                       >
//                         🗑️
//                       </Button>
//                     </td>
//                   </tr>
//                 ))
//               ) : (
//                 <tr>
//                   <td
//                     colSpan={8}
//                     style={{ textAlign: "center", padding: "20px" }}
//                   >
//                     No risks identified yet.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </Box>
//       </Card>

//       {/* Drawer for Add/Edit */}
//       <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
//         <Box sx={{ width: 400, p: 3 }}>
//           <Box display="flex" justifyContent="space-between" mb={2}>
//             <Typography variant="h6">
//               {editIndex !== null ? "Edit Risk" : "Add Risks"}
//             </Typography>
//             <IconButton onClick={handleCloseDrawer}>
//               <Close />
//             </IconButton>
//           </Box>

//           <TextField
//             label="Risk Name"
//             fullWidth
//             value={newRisk.title}
//             onChange={(e) => setNewRisk({ ...newRisk, title: e.target.value })}
//             sx={{ mb: 2 }}
//           />
//           <TextField
//             select
//             fullWidth
//             label="Category"
//             value={newRisk.category}
//             onChange={(e) =>
//               setNewRisk({ ...newRisk, category: e.target.value })
//             }
//             sx={{ mb: 2 }}
//           >
//             {taxonomy.categories.map((c) => (
//               <MenuItem key={c} value={c}>
//                 {c}
//               </MenuItem>
//             ))}
//           </TextField>
//           <TextField
//             select
//             fullWidth
//             label="Severity"
//             value={newRisk.severity}
//             onChange={(e) =>
//               setNewRisk({ ...newRisk, severity: e.target.value })
//             }
//             sx={{ mb: 2 }}
//           >
//             {taxonomy.severityLevels.map((s) => (
//               <MenuItem key={s} value={s}>
//                 {s}
//               </MenuItem>
//             ))}
//           </TextField>
//           <TextField
//             select
//             fullWidth
//             label="Likelihood"
//             value={newRisk.likelihood}
//             onChange={(e) =>
//               setNewRisk({ ...newRisk, likelihood: e.target.value })
//             }
//             sx={{ mb: 2 }}
//           >
//             {taxonomy.likelihoodLevels.map((l) => (
//               <MenuItem key={l} value={l}>
//                 {l}
//               </MenuItem>
//             ))}
//           </TextField>
//           <TextField
//             label="Score"
//             fullWidth
//             value={newRisk.score}
//             onChange={(e) => setNewRisk({ ...newRisk, score: e.target.value })}
//             sx={{ mb: 2 }}
//           />
//           <TextField
//             select
//             fullWidth
//             label="Owner"
//             value={newRisk.owner}
//             onChange={(e) => setNewRisk({ ...newRisk, owner: e.target.value })}
//             sx={{ mb: 2 }}
//           >
//             {taxonomy.owners.map((o) => (
//               <MenuItem key={o} value={o}>
//                 {o}
//               </MenuItem>
//             ))}
//           </TextField>
//           <TextField
//             select
//             fullWidth
//             label="Status"
//             value={newRisk.status}
//             onChange={(e) => setNewRisk({ ...newRisk, status: e.target.value })}
//             sx={{ mb: 2 }}
//           >
//             {taxonomy.statuses.map((s) => (
//               <MenuItem key={s} value={s}>
//                 {s}
//               </MenuItem>
//             ))}
//           </TextField>
//           <TextField
//             label="Description"
//             fullWidth
//             multiline
//             minRows={3}
//             value={newRisk.description}
//             onChange={(e) =>
//               setNewRisk({ ...newRisk, description: e.target.value })
//             }
//             sx={{ mb: 3 }}
//           />

//           <Button
//             variant="contained"
//             color="primary"
//             fullWidth
//             onClick={handleSaveRisk}
//           >
//             {editIndex !== null ? "Update" : "Add"} Risk
//           </Button>
//         </Box>
//       </Drawer>

//       {/* Navigation Buttons */}
//       <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
//         <Button variant="outlined">Save Draft</Button>
//         <Button
//           variant="contained"
//           color="primary"
//           onClick={() =>
//             navigate("/metrics-definition", {
//               state: {
//                 applicationSummary: {
//                   applicationName,
//                   archetypeCode: archetype,
//                   riskTier: riskLevel,
//                   domain,
//                   description,
//                 },
//                 trustWeights: attributes,
//                 risks,
//               },
//             })
//           }
//         >
//           Metrics Definition →
//         </Button>
//       </Box>
//     </Box>
//   );
// };

// export default RiskIdentification;

// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   Grid,
//   Paper,
//   Button,
//   Chip,
//   Divider,
//   Stack,
//   Card,
//   CardContent,
//   CircularProgress,
//   Alert,
//   Accordion,
//   AccordionSummary,
//   AccordionDetails,
// } from '@mui/material';
// import { InfoOutlined, WarningAmberRounded, ExpandMore } from '@mui/icons-material';
// import axios from 'axios';
// import { useNavigate, useLocation } from 'react-router-dom';

// const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// const CATEGORIES = ['Accuracy', 'Fairness', 'Privacy', 'Ethics', 'Robustness', 'Security', 'Reliability'];
// const SEVERITY_LEVELS = ['Critical', 'High', 'Medium', 'Low'];
// const SIDEBAR_WIDTH = 64; // Collapsed width (icons only) - expands to 260px on hover

// const RiskIdentification = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   console.log("📩 Received state from ApplicationSetupPage:", location.state);
//   // const { application, classificationResult } = location.state || {};

//   // const { application, classificationResult, riskCode, riskLevel } = location.state || {};
//   // const { application, riskLevel, archetype, description } = location.state || {};
//   const {
//     applicationName,
//     domain,
//     archetype,
//     riskCode,
//     riskLevel,
//     description,
//     trustWeights
//   } = location.state || {};

//   console.log("🔍 applicationName:", applicationName);
//   console.log("🔍 Domain:", domain);
//   console.log("🔍 archetype:", archetype);
//   console.log("🔍 Risk code:", riskCode);
//   console.log("🔍 Risk Level:", riskLevel);
//   console.log("🔍 description", description);

//   const [applicationSetup, setApplicationSetup] = useState(applicationName || null);
//   // const [applicationSetup, setApplicationSetup] = useState(null);
//   const [risks, setRisks] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [identifying, setIdentifying] = useState(false);
//   const [error, setError] = useState(null);
//   const [successMessage, setSuccessMessage] = useState(null);
//   const [categoryCounts, setCategoryCounts] = useState({});
//   const [severityCounts, setSeverityCounts] = useState({});

//   // const [attributes, setAttributes] = useState({});
//  const [attributes, setAttributes] = useState(
//   trustWeights || {
//     Accuracy: 0,
//     Fairness: 0,
//     Privacy: 0,
//     Ethics: 0,
//     Robustness: 0,
//     Security: 0,
//     Reliability: 0,
//   }
// );

//   // Normalize incoming data
//   // const riskData = classificationResult?.riskProfile || application?.riskProfile || {};
//   // const classData = classificationResult?.classification || application?.classification || {};
//   // const basicInfo = classificationResult?.data?.applicationSetup?.basicInfo || {};
//   // const domain = basicInfo?.domain || "Not specified";

//   // Fetch application setup data and existing risks on component mount
//   useEffect(() => {
//     const fetchData = async () => {
//       setLoading(true);
//       try {
//         const response = await axios.get(`${baseUrl}/risk-identification`);
//         if (response.data.success) {
//           if (response.data.previousStage) {
//             setApplicationSetup(response.data.previousStage);
//           }
//           if (response.data.data) {
//             setRisks(response.data.data.risks || []);
//             // setAttributes(response.data.data.attributes || {});
//           }

//           // if (response.data.data && response.data.data.risks) {
//           //   setRisks(response.data.data.risks);
//           // }
//         }
//       } catch (err) {
//         console.error('Error fetching data:', err);
//         setError('Failed to load data');
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchData();
//   }, []);

//   // Update category and severity counts when risks change
//   useEffect(() => {
//     const counts = {};
//     const severities = {};

//     CATEGORIES.forEach(cat => counts[cat] = 0);
//     SEVERITY_LEVELS.forEach(sev => severities[sev] = 0);

//     risks.forEach(risk => {
//       if (risk.category && counts.hasOwnProperty(risk.category)) {
//         counts[risk.category]++;
//       }
//       if (risk.severity && severities.hasOwnProperty(risk.severity)) {
//         severities[risk.severity]++;
//       }
//     });

//     setCategoryCounts(counts);
//     setSeverityCounts(severities);
//   }, [risks]);

//   // Handle Identify Risks button click
//   const handleIdentifyRisks = async () => {
//       // Validate required data before sending
//     if (!applicationName || !archetype || !riskLevel || !domain || !description) {
//       setError('Application Summary data is incomplete. Please check the previous step.');
//       return;
//     }
//     setIdentifying(true);
//     setError(null);
//     setSuccessMessage(null);
//     // reset attributes before request
//     // setAttributes({
//     //   Accuracy: 0,
//     //   Fairness: 0,
//     //   Privacy: 0,
//     //   Ethics: 0,
//     //   Robustness: 0,
//     //   Security: 0,
//     //   Reliability: 0,
//     // });

//     // console.log('Identifying risks...', { applicationSetup });

//     try {
//     // ✅ Build the payload dynamically from Application Summary
//     const requestPayload = {
//       applicationSummary: {
//         applicationName: applicationName,
//         archetypeCode: archetype,
//         riskTier: riskLevel,
//         domain: domain,
//         description: description
//       },
//       trustWeights: attributes,
//     };

//       console.log('Sending request to backend:', requestPayload);

//       const response = await axios.post(`${baseUrl}/risk-identification`, requestPayload);

//       console.log('Backend response:', response.data);
//       if (response.data.success && response.data.data) {
//       const { risks, attributes: newAttributes } = response.data.data;

//       setRisks(risks || []);
//       setAttributes(newAttributes || attributes);

//       if (risks?.length > 0) {
//         setSuccessMessage(`✅ Successfully identified ${risks.length} risks!`);
//       } else {
//         setError('No risks identified for this application.');
//       }
//     } else {
//       setError(response.data.error || 'Failed to identify risks.');
//     }
//   } catch (err) {
//     console.error('❌ Error identifying risks:', err);
//     setError(err.response?.data?.error || err.message || 'Failed to identify risks.');
//   } finally {
//     setIdentifying(false);
//   }
// };

//   //       if (response.data.success) {
//   //       const identifiedRisks = response.data.data?.risks || [];
//   //       const identifiedAttributes = response.data.data?.attributes || {};
//   //       setRisks(identifiedRisks);
//   //       setAttributes(identifiedAttributes);

//   //       if (identifiedRisks.length > 0) {
//   //         setSuccessMessage(`Successfully identified ${identifiedRisks.length} risk${identifiedRisks.length > 1 ? 's' : ''}!`);
//   //       } else {
//   //         setError('No risks were identified for the given Application Summary.');
//   //       }
//   //     } else {
//   //       setError(response.data.error || 'Failed to identify risks.');
//   //     }
//   //   } catch (err) {
//   //     console.error('❌ Error identifying risks:', err);
//   //     setError(err.response?.data?.error || err.message || 'Failed to identify risks.');
//   //   } finally {
//   //     setIdentifying(false);
//   //   }
//   // };

//   //     if (response.data.success) {
//   //       if (response.data.data && response.data.data.risks) {
//   //         const identifiedRisks = response.data.data.risks;
//   //         console.log(`Successfully identified ${identifiedRisks.length} risks:`, identifiedRisks);
//   //         setRisks(identifiedRisks);

//   //         // Show success feedback
//   //         if (identifiedRisks.length > 0) {
//   //           setSuccessMessage(`Successfully identified ${identifiedRisks.length} risk${identifiedRisks.length > 1 ? 's' : ''}!`);
//   //           setError(null);
//   //           // Auto-hide success message after 5 seconds
//   //           setTimeout(() => setSuccessMessage(null), 5000);
//   //           console.log('Risks displayed successfully');
//   //         } else {
//   //           setError('No risks were identified. Please try again or check your application setup data.');
//   //           setSuccessMessage(null);
//   //         }
//   //       } else {
//   //         console.warn('Response missing risks data:', response.data);
//   //         setError('Response received but no risks found in the data.');
//   //         setSuccessMessage(null);
//   //       }
//   //     } else {
//   //       console.error('Backend returned success: false', response.data);
//   //       setError(response.data.error || 'Failed to identify risks. Please try again.');
//   //       setSuccessMessage(null);
//   //     }
//   //   } catch (err) {
//   //     console.error('Error identifying risks:', err);
//   //     console.error('Error details:', {
//   //       message: err.message,
//   //       response: err.response?.data,
//   //       status: err.response?.status,
//   //       statusText: err.response?.statusText
//   //     });

//   //     if (err.response?.status === 500) {
//   //       setError(err.response?.data?.error || 'Server error. Please check your OpenAI API key in the backend .env file.');
//   //     } else if (err.response?.status === 400) {
//   //       setError(err.response?.data?.error || 'Invalid request. Please check your application setup data.');
//   //     } else if (err.code === 'ECONNREFUSED') {
//   //       setError('Cannot connect to backend server. Please make sure the backend is running.');
//   //     } else {
//   //       setError(err.response?.data?.error || err.message || 'Failed to identify risks. Please check the console for details.');
//   //     }
//   //   } finally {
//   //     setIdentifying(false);
//   //     console.log('Identify risks process completed');
//   //   }
//   // };

//   // Get severity color
//   const getSeverityColor = (severity) => {
//     switch (severity) {
//       case 'Critical': return 'error';
//       case 'High': return 'warning';
//       case 'Medium': return 'info';
//       case 'Low': return 'success';
//       default: return 'default';
//     }
//   };

//   // Get category color
//   const getCategoryColor = (category) => {
//     const colorMap = {
//       'Accuracy': 'primary',
//       'Fairness': 'secondary',
//       'Privacy': 'error',
//       'Ethics': 'warning',
//       'Robustness': 'info',
//       'Security': 'error',
//       'Reliability': 'success'
//     };
//     return colorMap[category] || 'default';
//   };

//   // Generate display chips from application setup
//   const generateChips = () => {
//     const chips = [];
//     if (applicationSetup?.ragVector) chips.push('RAG');
//     if (applicationSetup?.interfaceType === 'UI') chips.push('UI');
//     if (applicationSetup?.interfaceType === 'API') chips.push('API');
//     if (applicationSetup?.authMethod) chips.push('AUTH');
//     if (applicationSetup?.integrationTools?.length > 0) chips.push('INTEGRATED');
//     return chips;
//   };

//   if (loading) {
//     return (
//       <Box
//         sx={{
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center',
//           height: '100vh',
//           ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` }, // start right after sidebar
//         }}
//       >
//         <CircularProgress />
//       </Box>
//     );
//   }

//   return (
//     <Box
//       sx={{
//         flex: 1,
//         p: 4,
//         overflow: 'auto',
//         bgcolor: '#fafafa',
//         minHeight: '100vh',
//         ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` }, // start right after sidebar
//       }}
//     >
//       {/* Header */}
//       <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
//         <Typography variant="h5" fontWeight="bold">
//           Risk Identification & Classification
//         </Typography>
//         {/* <Box display="flex" alignItems="center" gap={1}>
//           <img
//             src="https://cdn-icons-png.flaticon.com/512/3177/3177440.png"
//             alt="user"
//             width="36"
//             height="36"
//             style={{ borderRadius: '50%' }}
//           />
//         </Box> */}
//       </Box>

//       <Typography variant="body2" color="text.secondary" mb={3}>
//         Identify and classify potential risks to establish your risk register
//       </Typography>

//       {error && (
//         <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
//           {error}
//         </Alert>
//       )}

//       {successMessage && (
//         <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
//           {successMessage}
//         </Alert>
//       )}

//       {/* Inputs Section - Display Application Setup Data Dynamically */}
//       {/* <Grid container spacing={2}>
//       <Grid item xs={12} sm={6} md={2.4}>
//         <Typography variant="body2" color="text.secondary">
//           Application
//         </Typography>
//         <Typography fontWeight="bold">
//           {applicationSetup.application || 'Not specified'}
//         </Typography>
//       </Grid>

//       <Grid item xs={12} sm={6} md={2.4}>
//         <Typography variant="body2" color="text.secondary">
//           Archetype Code
//         </Typography>
//         <Typography fontWeight="bold">
//           {applicationSetup.archetypeCode || 'Not specified'}
//         </Typography>
//       </Grid>

//       <Grid item xs={12} sm={6} md={2.4}>
//         <Typography variant="body2" color="text.secondary">
//           Risk Tier
//         </Typography>
//         <Typography fontWeight="bold">
//           {applicationSetup.riskTier || 'Not specified'}
//         </Typography>
//       </Grid>

//       <Grid item xs={12} sm={6} md={2.4}>
//         <Typography variant="body2" color="text.secondary">
//           Domain
//         </Typography>
//         <Typography fontWeight="bold">
//           {applicationSetup.domain || 'Not specified'}
//         </Typography>
//       </Grid>

//       <Grid item xs={12} sm={6} md={2.4}>
//         <Typography variant="body2" color="text.secondary">
//           Modifiers
//         </Typography>
//         <Typography fontWeight="bold">
//           {applicationSetup.modifiers?.length > 0
//             ? applicationSetup.modifiers.join(', ')
//             : 'None'}
//         </Typography>
//       </Grid>
//     </Grid> */}

//         <Card sx={{ mb: 3, p: 2 }}>
//           <Typography variant="h6" gutterBottom>Application Summary</Typography>
//           <Grid container spacing={2}>
//             <Grid item xs={6} md={3}>
//               <Typography variant="body2" color="textSecondary">Application</Typography>
//               <Typography variant="subtitle2">{applicationName || 'Not specified'}</Typography>
//             </Grid>
//             <Grid item xs={6} md={3}>
//               <Typography variant="body2" color="textSecondary">Archetype Code</Typography>
//               <Typography variant="subtitle2">{archetype || 'Not specified'}</Typography>
//             </Grid>
//             <Grid item xs={6} md={3}>
//               <Typography variant="body2" color="textSecondary">Risk Tier</Typography>
//               <Typography variant="subtitle2">{riskLevel || 'Not specified'}</Typography>
//             </Grid>
//             <Grid item xs={6} md={3}>
//               <Typography variant="body2" color="textSecondary">Domain</Typography>
//               <Typography variant="subtitle2">{domain}</Typography>
//             </Grid>
//           </Grid>

//           <Box sx={{ mt: 2 }}>
//             <Typography variant="body2" color="textSecondary">Description</Typography>
//             <Typography variant="body2">
//               {description || 'Not specified'}
//             </Typography>
//           </Box>
//         </Card>

//       {/* <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
//         <Typography fontWeight="bold" mb={2}>
//           Inputs from Previous Stage (Application Setup)
//         </Typography>
//         {applicationSetup ? (
//           <>
//             <Grid container spacing={2}>
//               <Grid item xs={12} sm={6} md={3}>
//                 <Typography variant="body2" color="text.secondary">
//                   Interface Type
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {applicationSetup.interfaceType || 'Not specified'}
//                 </Typography>
//               </Grid>
//               <Grid item xs={12} sm={6} md={3}>
//                 <Typography variant="body2" color="text.secondary">
//                   LLM Provider
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {applicationSetup.llmProvider || 'Not specified'}
//                 </Typography>
//               </Grid>
//               <Grid item xs={12} sm={6} md={3}>
//                 <Typography variant="body2" color="text.secondary">
//                   LLM Model
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {applicationSetup.llmModel || 'Not specified'}
//                 </Typography>
//               </Grid>
//               <Grid item xs={12} sm={6} md={3}>
//                 <Typography variant="body2" color="text.secondary">
//                   Frontend Stack
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {applicationSetup.frontendStack || 'Not specified'}
//                 </Typography>
//               </Grid>
//               <Grid item xs={12} sm={6} md={3}>
//                 <Typography variant="body2" color="text.secondary">
//                   Backend Stack
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {applicationSetup.backendStack || 'Not specified'}
//                 </Typography>
//               </Grid>
//               <Grid item xs={12} sm={6} md={3}>
//                 <Typography variant="body2" color="text.secondary">
//                   RAG Vector DB
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {applicationSetup.ragVector || 'Not specified'}
//                 </Typography>
//               </Grid>
//               <Grid item xs={12} sm={6} md={3}>
//                 <Typography variant="body2" color="text.secondary">
//                   Auth Method
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {applicationSetup.authMethod || 'Not specified'}
//                 </Typography>
//               </Grid>
//               <Grid item xs={12} sm={6} md={3}>
//                 <Typography variant="body2" color="text.secondary">
//                   Integration Tools
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {applicationSetup.integrationTools?.length > 0
//                     ? applicationSetup.integrationTools.join(', ')
//                     : 'None'}
//                 </Typography>
//               </Grid>
//             </Grid>
//             <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" gap={1}>
//               {generateChips().map((chip, idx) => (
//                 <Chip key={idx} label={chip} color="primary" size="small" />
//               ))}
//             </Stack>
//           </>
//         ) : (
//           <Alert severity="warning">
//             No application setup data found. Please complete the Application Setup step first.
//           </Alert>
//         )}
//       </Paper> */}

//       {/* Identify Risks Button - Above Filter Section */}
//       <Box display="flex" justifyContent="flex-end" mb={2}>
//         <Button
//           variant="contained"
//           color="primary"
//           onClick={handleIdentifyRisks}
//           disabled={identifying || !applicationSetup}
//           startIcon={identifying ? <CircularProgress size={16} /> : null}
//           sx={{ minWidth: 180 }}
//         >
//           {identifying ? 'Identifying Risks...' : 'Identify Risks'}
//         </Button>
//       </Box>

//       {/* Filter Section */}
//       {/* <Paper variant="outlined" sx={{ p: 3, mb: 3 }}> */}
//       <Paper
//           variant="outlined"
//           sx={{
//             p: 3,
//             mb: 3,
//             borderRadius: 2,
//             backgroundColor: "#ffffff",
//           }}
//         >
//         <Typography fontWeight="bold" mb={2}>
//           Attributes
//         </Typography>

//         <Grid container spacing={2}>
//           {CATEGORIES.map((cat, i) => (
//             <Grid item xs={6} sm={3} md={1.7} key={i}>
//               <Box textAlign="center">
//                 <Typography variant="body2" fontWeight="bold">
//                   {cat}
//                 </Typography>
//                 <Typography variant="h6" color="text.secondary">
//                   {attributes[cat] !== undefined ? attributes[cat] : 0}
//                 </Typography>
//               </Box>
//             </Grid>
//           ))}
//         </Grid>
//         <Box display="flex" justifyContent="flex-end" mt={2}>
//     {/* <Button startIcon={<InfoOutlined />} sx={{ textTransform: 'none' }}>
//       View Taxonomy Details
//     </Button> */}
//   </Box>
// </Paper>

//         {/* <Button sx={{ mt: 2, textTransform: 'none' }}>View Taxonomy Details</Button>
//       </Paper> */}

//       Score and Risk Register
//       {/* <Grid container spacing={3}>
//         <Grid item xs={12} md={4}>
//           <Card variant="outlined">
//             <CardContent>
//               <Typography fontWeight="bold" mb={2}>
//                 Score Distribution
//               </Typography>
//               {SEVERITY_LEVELS.map((level, i) => (
//                 <Box
//                   key={i}
//                   display="flex"
//                   justifyContent="space-between"
//                   py={0.5}
//                   borderBottom={i !== 3 ? '1px solid #eee' : 'none'}
//                 >
//                   <Typography>{level}</Typography>
//                   <Typography fontWeight="bold">{severityCounts[level] || 0}</Typography>
//                 </Box>
//               ))}
//             </CardContent>
//           </Card>
//         </Grid>

//         <Grid item xs={12} md={8}>
//           {risks.length > 0 ? (
//             <Card variant="outlined">
//               <CardContent>
//                 <Typography fontWeight="bold" mb={2}>
//                   Identified Risks ({risks.length})
//                 </Typography>
//                 <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
//                   {risks.map((risk, index) => (
//                     <Accordion key={risk.id || index} sx={{ mb: 1 }}>
//                       <AccordionSummary expandIcon={<ExpandMore />}>
//                         <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
//                           <Typography fontWeight="bold" sx={{ flex: 1 }}>
//                             {risk.title}
//                           </Typography>
//                           <Chip
//                             label={risk.category}
//                             color={getCategoryColor(risk.category)}
//                             size="small"
//                           />
//                           <Chip
//                             label={risk.severity}
//                             color={getSeverityColor(risk.severity)}
//                             size="small"
//                           />
//                         </Box>
//                       </AccordionSummary>
//                       <AccordionDetails>
//                         <Typography variant="body2" mb={2}>
//                           <strong>Description:</strong> {risk.description}
//                         </Typography>
//                         {risk.mitigation && (
//                           <Typography variant="body2">
//                             <strong>Mitigation:</strong> {risk.mitigation}
//                           </Typography>
//                         )}
//                       </AccordionDetails>
//                     </Accordion>
//                   ))}
//                 </Box>
//               </CardContent>
//             </Card>
//           ) : (
//             <Card variant="outlined" sx={{ mt: 3 }}>
//   <CardContent>
//     <Typography fontWeight="bold" mb={2}>
//       Risk Register ({risks.length} Risks)
//     </Typography>

//     {risks.length > 0 ? (
//       <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
//         {risks.map((risk, index) => (
//           <Accordion key={index} sx={{ mb: 1 }}>
//             <AccordionSummary expandIcon={<ExpandMore />}>
//               <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
//                 <Typography fontWeight="bold" sx={{ flex: 1 }}>
//                   {risk.title}
//                 </Typography>
//                 <Chip label={risk.category} color={getCategoryColor(risk.category)} size="small" />
//                 <Chip label={risk.severity} color={getSeverityColor(risk.severity)} size="small" />
//               </Box>
//             </AccordionSummary>

//             <AccordionDetails>
//               <Typography variant="body2" mb={2}>
//                 <strong>Description:</strong> {risk.description || 'No description provided'}
//               </Typography>
//               <Typography variant="body2" mb={2}>
//                 <strong>Likelihood:</strong> {risk.likelihood || 'Unknown'}
//               </Typography>
//               <Typography variant="body2" mb={2}>
//                 <strong>Score:</strong> {risk.score || 'N/A'}
//               </Typography>
//               <Typography variant="body2" mb={2}>
//                 <strong>Owner:</strong> {risk.owner || 'AI Risk Manager'}
//               </Typography>
//               <Typography variant="body2" mb={2}>
//                 <strong>Status:</strong> {risk.status || 'Draft'}
//               </Typography>
//               {risk.mitigation && (
//                 <Typography variant="body2">
//                   <strong>Mitigation:</strong> {risk.mitigation}
//                 </Typography>
//               )}
//             </AccordionDetails>
//           </Accordion>
//         ))}
//       </Box>
//     ) : (
//       <Box textAlign="center" py={6}>
//         <WarningAmberRounded sx={{ fontSize: 40, color: '#1976d2', mb: 2 }} />
//         <Typography variant="h6" fontWeight="bold">
//           No risks identified yet
//         </Typography>
//         <Typography variant="body2" color="text.secondary" mb={2}>
//           Click “Identify Risks” to generate risks using AI
//         </Typography>
//         <Button
//           variant="outlined"
//           onClick={handleIdentifyRisks}
//           disabled={identifying || !applicationSetup}
//         >
//           {identifying ? 'Identifying...' : 'Identify Risks'}
//         </Button>
//       </Box>
//     )}
//   </CardContent>
// </Card>

//             // <Card variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
//             //   <WarningAmberRounded sx={{ fontSize: 40, color: '#1976d2', mb: 2 }} />
//             //   <Typography variant="h6" fontWeight="bold">
//             //     No risks identified yet
//             //   </Typography>
//             //   <Typography variant="body2" color="text.secondary" mb={2}>
//             //     Click "Identify Risks" to generate risks using AI
//             //   </Typography>
//             //   <Button
//             //     variant="outlined"
//             //     onClick={handleIdentifyRisks}
//             //     disabled={identifying || !applicationSetup}
//             //   >
//             //     {identifying ? 'Identifying...' : 'Identify Risks'}
//             //   </Button>
//             // </Card>
//           )}
//         </Grid>
//       </Grid> */}
//       {/* Risk Register Section */}
// <Card
//   variant="outlined"
//   sx={{
//     mt: 3,
//     p: 3,
//     borderRadius: 2,
//     backgroundColor: "#ffffff",
//     boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
//   }}
// >
//   <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
//     <Typography fontWeight="bold">
//       Risk Register ({risks.length} {risks.length === 1 ? 'Risk' : 'Risks'})
//       {/* Risk Register ({risks.length} Risks) */}
//     </Typography>

//     {/* Search + Add */}
//     <Box display="flex" alignItems="center" gap={2}>
//       <Box
//         component="input"
//         placeholder="Search"
//         style={{
//           border: "1px solid #ccc",
//           borderRadius: "20px",
//           padding: "6px 12px",
//           width: "180px",
//           outline: "none",
//         }}
//       />
//       <Button
//         sx={{
//           textTransform: "none",
//           color: "#1976d2",
//           fontWeight: 500,
//           fontSize: 13,
//         }}
//       >
//         + Add more risks
//       </Button>
//     </Box>
//   </Box>

//   {/* Table Layout */}
//   <Box sx={{ overflowX: "auto" }}>
//     <table
//       style={{
//         width: "100%",
//         borderCollapse: "collapse",
//         fontSize: "14px",
//       }}
//     >
//       <thead>
//         <tr
//           style={{
//             borderBottom: "1px solid #e0e0e0",
//             textAlign: "left",
//             background: "#fafafa",
//           }}
//         >
//           {[
//             "Risk Name",
//             "Category",
//             "Severity",
//             "Likelihood",
//             "Score",
//             "Owner",
//             "Status",
//             "Actions",
//           ].map((header, i) => (
//             <th
//               key={i}
//               style={{
//                 padding: "10px 8px",
//                 fontWeight: 600,
//                 color: "#333",
//               }}
//             >
//               {header}
//             </th>
//           ))}
//         </tr>
//       </thead>

//       <tbody>
//         {risks.length > 0 ? (
//           risks.map((risk, index) => (
//             <tr
//               key={risk.id || index}
//               style={{
//                 borderBottom: "1px solid #f0f0f0",
//               }}
//             >
//               {/* Risk Name */}
//               <td
//                 style={{
//                   padding: "10px 8px",
//                   color: "#1976d2",
//                   fontWeight: 500,
//                   cursor: "pointer",
//                 }}
//               >
//                 {risk.title || "Unnamed Risk"}
//               </td>

//               {/* Category */}
//               <td style={{ padding: "10px 8px" }}>{risk.category}</td>

//               {/* Severity */}
//               <td style={{ padding: "10px 8px" }}>
//                 <Chip
//                   label={risk.severity}
//                   size="small"
//                   sx={{
//                     backgroundColor:
//                       risk.severity === "High"
//                         ? "#ffebee"
//                         : risk.severity === "Medium"
//                         ? "#fff8e1"
//                         : "#e8f5e9",
//                     color:
//                       risk.severity === "High"
//                         ? "#d32f2f"
//                         : risk.severity === "Medium"
//                         ? "#f57c00"
//                         : "#388e3c",
//                     fontWeight: 500,
//                   }}
//                 />
//               </td>

//               {/* Likelihood */}
//               <td style={{ padding: "10px 8px" }}>
//                 <Chip
//                   label={risk.likelihood || "Occasional"}
//                   size="small"
//                   sx={{
//                     backgroundColor:
//                       risk.likelihood === "High"
//                         ? "#ffebee"
//                         : "#e3f2fd",
//                     color:
//                       risk.likelihood === "High"
//                         ? "#d32f2f"
//                         : "#1565c0",
//                   }}
//                 />
//               </td>

//               {/* Score */}
//               <td style={{ padding: "10px 8px" }}>
//                 <Chip
//                   label={risk.score || "Medium"}
//                   size="small"
//                   sx={{
//                     backgroundColor: "#fff3e0",
//                     color: "#ef6c00",
//                     fontWeight: 500,
//                   }}
//                 />
//               </td>

//               {/* Owner */}
//               <td style={{ padding: "10px 8px" }}>
//                 {risk.owner || "AI Risk Manager"}
//               </td>

//               {/* Status */}
//               <td style={{ padding: "10px 8px" }}>
//                 {risk.status || "Draft"}
//               </td>

//               {/* Actions */}
//               <td
//                 style={{
//                   padding: "10px 8px",
//                   display: "flex",
//                   alignItems: "center",
//                   gap: "6px",
//                 }}
//               >
//                 <Button
//                   size="small"
//                   sx={{
//                     color: "#1976d2",
//                     minWidth: 0,
//                     fontSize: 14,
//                     p: 0.5,
//                   }}
//                 >
//                   ✏️
//                 </Button>
//                 <Button
//                   size="small"
//                   sx={{
//                     color: "#d32f2f",
//                     minWidth: 0,
//                     fontSize: 14,
//                     p: 0.5,
//                   }}
//                 >
//                   🗑️
//                 </Button>
//               </td>
//             </tr>
//           ))
//         ) : (
//           <tr>
//             <td
//               colSpan={8}
//               style={{
//                 textAlign: "center",
//                 padding: "20px",
//                 color: "#999",
//               }}
//             >
//               No risks identified yet
//             </td>
//           </tr>
//         )}
//       </tbody>
//     </table>
//   </Box>
// </Card>

// {/* Score Distribution (below table) */}
// <Card
//   variant="outlined"
//   sx={{
//     mt: 3,
//     p: 3,
//     borderRadius: 2,
//     backgroundColor: "#ffffff",
//     boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
//   }}
// >
//   <Typography fontWeight="bold" mb={2}>
//     Score Distribution
//   </Typography>
//   {SEVERITY_LEVELS.map((level, i) => (
//     <Box
//       key={i}
//       display="flex"
//       justifyContent="space-between"
//       py={0.5}
//       borderBottom={i !== 3 ? "1px solid #eee" : "none"}
//     >
//       <Typography>{level}</Typography>
//       <Typography fontWeight="bold">{severityCounts[level] || 0}</Typography>
//     </Box>
//   ))}
// </Card>

//       {/* Bottom Buttons */}
//       <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
//         <Button variant="outlined">Save Draft</Button>
//         <Button
//   variant="contained"
//   color="primary"
//   onClick={() =>
//     navigate("/metrics-definition", {
//           state: {
//             applicationSummary: {
//               applicationName,
//               archetypeCode: archetype,
//               riskTier: riskLevel,
//               domain,
//               description,
//             },
//             trustWeights: attributes,
//             risks,
//           },
//         })
//       }
//     >
//       Metrics Definition →
//     </Button>

//         {/* <Button
//           variant="contained"
//           color="primary"
//           onClick={() => navigate('/metrics-definition')}
//         >
//           Metrics Definition →
//         </Button> */}
//       </Box>
//     </Box>
//   );
// };

// export default RiskIdentification;
